import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardHeader } from './DashboardHeader';
import { Card } from './ui/card';
import { AnalysisNav } from './ui/analysis-nav';
import { StarsBackground } from './ui/stars-background';
import { 
    LayoutTemplate, 
    LineChart, 
    AlertCircle, 
    CheckCircle, 
    XCircle,
    Lightbulb,
    ArrowRight,
    Award,
    List,
    FileText,
    BarChart4,
    Layers,
    PieChart
} from 'lucide-react';
import { motion } from "framer-motion";
import { fadeIn, slideIn } from "../utils/motion";
import { Progress } from './ui/progress';
import { cn } from "@/lib/utils";

interface StructureAnalysis {
    overall_score: number;
    slide_flow: string;
    clarity: string;
    visual_appeal: string;
}

interface SpecificExample {
    current_element: string;
    suggested_change: string;
    rationale: string;
}

interface SlideBreakdown {
    slide_type: string;
    effectiveness: number;
    strengths: string[];
    weaknesses: string[];
    specific_examples: SpecificExample[];
}

interface Recommendation {
    area: string;
    suggestion: string;
    impact: string;
    example: string;
}

interface PresentationAnalysisData {
    structure_analysis: StructureAnalysis;
    slide_breakdown: SlideBreakdown[];
    missing_elements: string[];
    recommendations: Recommendation[];
    best_practices: string[];
}

interface AnalysisResponse {
    id: number;
    presentation_analysis: PresentationAnalysisData;
    created_at: string;
}

const CustomProgress: React.FC<{
  value: number;
  className?: string;
  indicatorColor?: string;
}> = ({ value, className, indicatorColor }) => {
  return (
    <div className={cn("relative h-1.5 w-full overflow-hidden rounded-full bg-primary/10", className)}>
      <div 
        className={cn("h-full flex-1 transition-all", indicatorColor || "bg-primary")}
        style={{ 
          width: `${value}%`,
          backgroundColor: indicatorColor
        }}
      />
    </div>
  );
};

const PresentationAnalysis: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { api, isAuthenticated } = useAuth();
    const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);

    useEffect(() => {
        const fetchAnalysis = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/api/presentation-analysis/${id}`);
                setAnalysis(response.data);
                setError(null);
                
                // Check if the analysis is being generated for the first time
                if (response.data.is_generating) {
                    setIsGenerating(true);
                    // Poll every 5 seconds to check if analysis is complete
                    const interval = setInterval(async () => {
                        try {
                            const updatedResponse = await api.get(`/api/presentation-analysis/${id}`);
                            if (!updatedResponse.data.is_generating) {
                                setAnalysis(updatedResponse.data);
                                setIsGenerating(false);
                                clearInterval(interval);
                            }
                        } catch (err) {
                            // Handle error but don't clear interval
                            console.error("Error polling for analysis:", err);
                        }
                    }, 5000);
                    
                    // Clean up interval on component unmount
                    return () => clearInterval(interval);
                }
            } catch (err: any) {
                setError(err.response?.data?.message || 'Failed to fetch presentation analysis');
                console.error('Error fetching presentation analysis:', err);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) {
            fetchAnalysis();
        }
    }, [api, id, isAuthenticated]);

    const renderContent = () => {
        if (loading) {
            return <div className="text-center mt-8">Loading presentation analysis...</div>;
        }

        if (isGenerating) {
            return (
                <div className="text-center mt-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-lg font-medium">Generating presentation analysis...</p>
                    <p className="text-sm text-muted-foreground mt-2">This may take a minute or two.</p>
                </div>
            );
        }

        if (error) {
            return <div className="text-center mt-8 text-red-500">{error}</div>;
        }

        if (!analysis?.presentation_analysis) {
            return <div className="text-center mt-8">No presentation analysis available.</div>;
        }

        const { 
            structure_analysis, 
            slide_breakdown, 
            missing_elements, 
            recommendations, 
            best_practices 
        } = analysis.presentation_analysis;

        return (
            <div className="space-y-16">
                {/* Overall Structure Analysis */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.3, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                <LayoutTemplate className="mr-2 text-blue-500" size={26} />
                                Overall Structure Analysis
                            </h2>
                            <div className="space-y-6 text-left">
                                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                                    <div className="w-full md:w-1/3 flex flex-col items-center justify-center p-4 bg-white/5 rounded-lg">
                                        <div className="text-5xl font-bold text-blue-600 mb-2">
                                            {structure_analysis.overall_score}
                                        </div>
                                        <div className="text-sm text-gray-600 text-center">Overall Score</div>
                                        <CustomProgress 
                                            value={structure_analysis.overall_score} 
                                            className="w-full h-2 mt-2" 
                                        />
                                    </div>
                                    <div className="w-full md:w-2/3 space-y-4">
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                <LineChart className="mr-2 text-indigo-500" size={18} />
                                                Slide Flow
                                            </h3>
                                            <p className="text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{structure_analysis.slide_flow}</p>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                <FileText className="mr-2 text-emerald-500" size={18} />
                                                Clarity
                                            </h3>
                                            <p className="text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{structure_analysis.clarity}</p>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                <PieChart className="mr-2 text-purple-500" size={18} />
                                                Visual Appeal
                                            </h3>
                                            <p className="text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{structure_analysis.visual_appeal}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* Slide Breakdown */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.5, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                <Layers className="mr-2 text-orange-500" size={26} />
                                Slide Breakdown
                            </h2>
                            <div className="space-y-6 text-left">
                                {slide_breakdown.map((slide, index) => (
                                    <div key={index} className="p-4 bg-white/5 rounded-lg">
                                        <div className="flex justify-between items-center mb-2">
                                            <h3 className="text-lg font-semibold" style={{ fontFamily: '"Open Sans", sans-serif' }}>{slide.slide_type}</h3>
                                            <div className="flex items-center">
                                                <span className="text-base font-medium mr-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>Effectiveness:</span>
                                                <span className={`text-base font-bold ${
                                                    slide.effectiveness >= 70 ? 'text-green-500' : 
                                                    slide.effectiveness >= 40 ? 'text-yellow-500' : 
                                                    'text-red-500'
                                                }`} style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                    {slide.effectiveness}%
                                                </span>
                                            </div>
                                        </div>
                                        <CustomProgress 
                                            value={slide.effectiveness} 
                                            className="w-full h-1.5 mb-3" 
                                            indicatorColor={
                                                slide.effectiveness >= 70 ? 'rgb(34, 197, 94)' : 
                                                slide.effectiveness >= 40 ? 'rgb(234, 179, 8)' : 
                                                'rgb(239, 68, 68)'
                                            }
                                        />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                                            <div>
                                                <h4 className="text-base font-medium flex items-center text-green-600" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                    <CheckCircle className="mr-1" size={16} />
                                                    Strengths
                                                </h4>
                                                <ul className="mt-1 space-y-1">
                                                    {slide.strengths.map((strength, i) => (
                                                        <li key={i} className="text-base text-gray-700 flex items-start" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                            <span className="text-green-500 mr-1">•</span>
                                                            {strength}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div>
                                                <h4 className="text-base font-medium flex items-center text-red-600" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                    <XCircle className="mr-1" size={16} />
                                                    Weaknesses
                                                </h4>
                                                <ul className="mt-1 space-y-1">
                                                    {slide.weaknesses.map((weakness, i) => (
                                                        <li key={i} className="text-base text-gray-700 flex items-start" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                            <span className="text-red-500 mr-1">•</span>
                                                            {weakness}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                        {slide.specific_examples && slide.specific_examples.length > 0 && (
                                            <div className="mt-4 border-t pt-3">
                                                <h5 className="text-base font-medium text-gray-900" style={{ fontFamily: '"Open Sans", sans-serif' }}>Specific Improvement Examples:</h5>
                                                {slide.specific_examples.map((example, idx) => (
                                                    <div key={idx} className="mt-2 bg-purple-50 p-3 rounded-md">
                                                        <div className="flex items-start mb-1">
                                                            <span className="text-base font-medium text-gray-700 w-24" style={{ fontFamily: '"Open Sans", sans-serif' }}>Current:</span>
                                                            <span className="text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{example.current_element}</span>
                                                        </div>
                                                        <div className="flex items-start mb-1">
                                                            <span className="text-base font-medium text-gray-700 w-24" style={{ fontFamily: '"Open Sans", sans-serif' }}>Suggestion:</span>
                                                            <span className="text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{example.suggested_change}</span>
                                                        </div>
                                                        <div className="flex items-start">
                                                            <span className="text-base font-medium text-gray-700 w-24" style={{ fontFamily: '"Open Sans", sans-serif' }}>Why:</span>
                                                            <span className="text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{example.rationale}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* Missing Elements & Recommendations */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.7, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                <AlertCircle className="mr-2 text-red-500" size={26} />
                                Missing Elements & Recommendations
                            </h2>
                            <div className="space-y-6 text-left">
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                        <List className="mr-2 text-red-500" size={18} />
                                        Missing Elements
                                    </h3>
                                    <ul className="space-y-2">
                                        {missing_elements.map((element, index) => (
                                            <li key={index} className="flex items-start">
                                                <span className="text-red-500 mr-2">•</span>
                                                <span className="text-gray-700 text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{element}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold mb-2 flex items-center" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                        <Lightbulb className="mr-2 text-yellow-500" size={18} />
                                        Recommendations
                                    </h3>
                                    <div className="space-y-4">
                                        {recommendations.map((rec, index) => (
                                            <div key={index} className="p-3 bg-white/10 rounded-lg">
                                                <h4 className="font-medium text-gray-900 text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{rec.area}</h4>
                                                <div className="flex items-start mt-1">
                                                    <ArrowRight className="text-gray-500 mr-2 mt-1 flex-shrink-0" size={16} />
                                                    <p className="text-base text-gray-700" style={{ fontFamily: '"Open Sans", sans-serif' }}>{rec.suggestion}</p>
                                                </div>
                                                <div className="mt-2 text-base text-gray-600 italic" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                                    Impact: {rec.impact}
                                                </div>
                                                {rec.example && (
                                                     <div className="mt-2 bg-purple-50 p-3 rounded-md">
                                                        <span className="text-base font-medium text-gray-900 block mb-1" style={{ fontFamily: '"Open Sans", sans-serif' }}>Example Implementation:</span>
                                                        <span className="text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{rec.example}</span>
                                                     </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                {/* Best Practices */}
                <motion.div
                    variants={fadeIn("up", "spring", 0.9, 0.75)}
                    initial="hidden"
                    animate="show"
                    className="w-full"
                >
                    <Card className="p-6 relative bg-transparent border border-primary/10 shadow-2xl backdrop-blur-sm rounded-xl">
                        <div className="absolute inset-0 -z-10 overflow-hidden rounded-xl">
                            <StarsBackground />
                        </div>
                        <div className="relative z-10">
                            <h2 className="text-2xl font-bold mb-4 text-gray-900 flex items-center text-left border-b border-primary/20 pb-2" style={{ fontFamily: '"Open Sans", sans-serif' }}>
                                <Award className="mr-2 text-amber-500" size={26} />
                                Best Practices
                            </h2>
                            <div className="space-y-2 text-left">
                                {best_practices.map((practice, index) => (
                                    <div key={index} className="flex items-start p-2">
                                        <span className="text-amber-500 mr-2 mt-1">•</span>
                                        <p className="text-gray-700 text-base" style={{ fontFamily: '"Open Sans", sans-serif' }}>{practice}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>
                </motion.div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-background relative overflow-x-hidden">
            {/* Stars Background */}
            <div className="fixed inset-0 -z-10">
                <StarsBackground />
            </div>
            
            {/* Content */}
            <div className="relative z-10">
                {/* DashboardHeader is already included in the ProtectedRoute wrapper */}
                <AnalysisNav id={id || ''} activeTab="presentation" />
                <motion.div 
                    variants={slideIn("left", "tween", 0.2, 1)}
                    initial="hidden"
                    animate="show"
                    className="max-w-6xl mx-auto px-4 py-8"
                >
                    {renderContent()}
                </motion.div>
            </div>
        </div>
    );
};

export default PresentationAnalysis; 